import { stripHeaders, redactFields, redactQueryString } from './keyRedactor';
import { scrub, scrubStringsIn } from './stringPatternScrubber';

/**
 * Why: Sentry's `beforeSend` callback is the last line of defense before
 * a browser event leaves the client. This module orchestrates the PII
 * scrubbing pipeline — delegating key-based redaction to keyRedactor and
 * regex scrubbing to stringPatternScrubber, then handling event-level
 * concerns (breadcrumb rebuild, exception scrubbing) that neither owns
 * alone.
 * Scope: Changes to individual rules live in the two helper modules.
 * Changes to the pipeline — ordering, new event sections — land here.
 * Wired via `Sentry.init({ beforeSend })` in `src/index.js`.
 * SRP: Scrub a single Sentry event. Return the scrubbed event, or null
 * to drop it.
 *
 * Mirrors `app/Sentry/EventScrubber.php` in the API repo.
 */

/**
 * Why: Sentry calls this once per outbound event. The pipeline must be
 * defensive — an uncaught exception here silently drops the event.
 * Ordering matters: scrub request data first so downstream processing
 * works on already-cleaned content.
 * Scope: Adding a new scrubbable section (e.g. `extra`) goes here with
 * its own helper. The pipeline stays the ordering authority.
 * SRP: Run the full scrub pipeline on one event.
 *
 * @param {Object} event Sentry event shape (see https://develop.sentry.dev/sdk/data-model/event-payloads/)
 * @returns {Object|null} Scrubbed event, or null to drop
 */
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') {
    return event;
  }
  scrubRequest(event);
  scrubMessage(event);
  scrubExceptions(event);
  scrubBreadcrumbs(event);
  return event;
}

/**
 * Why: Request data is a PII surface whenever the browser captures XHR
 * or fetch payloads. Headers, body, and query string each have their
 * own shape — dispatch to the right helper.
 * Scope: Request fields Sentry adds later route through additional calls.
 * SRP: Run key-based redaction over every section of `event.request`.
 *
 * @param {Object} event Event whose `request` property will be mutated
 */
function scrubRequest(event) {
  if (!event.request || typeof event.request !== 'object') {
    return;
  }
  if (event.request.headers) {
    event.request.headers = stripHeaders(event.request.headers);
  }
  if (event.request.data) {
    event.request.data = scrubBody(event.request.data);
  }
  if (typeof event.request.query_string === 'string') {
    event.request.query_string = redactQueryString(event.request.query_string);
  }
}

/**
 * Why: Request bodies come in two shapes — parsed objects from JSON
 * requests, and raw strings from webhooks or unparsed payloads. Each
 * needs a different scrubber.
 * Scope: If Sentry begins capturing objects (e.g. FormData) in `data`,
 * a new guard lands here.
 * SRP: Dispatch body scrubbing to the right helper based on shape.
 */
function scrubBody(data) {
  if (data === null || data === undefined) {
    return data;
  }
  if (typeof data === 'object') {
    return redactFields(data);
  }
  if (typeof data === 'string') {
    return scrub(data);
  }
  return data;
}

/**
 * Why: Exception/error messages often include user-provided values.
 * Key-based redaction can't help — we're scrubbing free-form text. Regex
 * patterns are the right tool.
 * Scope: Message-level transformations land here.
 * SRP: Run pattern scrubbing over the event's top-level message.
 */
function scrubMessage(event) {
  if (event.message && typeof event.message === 'string') {
    event.message = scrub(event.message);
    return;
  }
  if (event.message && typeof event.message === 'object' && typeof event.message.message === 'string') {
    event.message.message = scrub(event.message.message);
  }
}

/**
 * Why: Exception values carry the thrown Error's message verbatim. PII
 * in that string — say, from a lookup failure — would ship to Sentry
 * unfiltered without this pass.
 * Scope: If stack frame `vars` ever get enabled, they'd need their own
 * sanitization pass here too.
 * SRP: Run pattern scrubbing over every exception value in the event.
 */
function scrubExceptions(event) {
  if (!event.exception || !Array.isArray(event.exception.values)) {
    return;
  }
  event.exception.values.forEach((exceptionEntry) => {
    if (typeof exceptionEntry.value === 'string') {
      exceptionEntry.value = scrub(exceptionEntry.value);
    }
  });
}

/**
 * Why: Breadcrumbs carry metadata across the wire. XHR breadcrumbs
 * include URLs + query strings; console breadcrumbs include arbitrary
 * log text; navigation breadcrumbs include URLs. Each is a potential
 * leak.
 * Scope: Sentry adds new breadcrumb types that follow the same
 * data-object pattern — this loop handles them without change.
 * SRP: Rebuild breadcrumb metadata + message on the event with scrubbed
 * copies.
 */
function scrubBreadcrumbs(event) {
  if (!Array.isArray(event.breadcrumbs)) {
    return;
  }
  event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => scrubBreadcrumb(breadcrumb));
}

/**
 * Why: Per-breadcrumb scrubbing is its own concern and testable in
 * isolation.
 * Scope: Adding sentry's new breadcrumb fields lands here.
 * SRP: Return a scrubbed copy of one breadcrumb.
 */
function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb || typeof breadcrumb !== 'object') {
    return breadcrumb;
  }
  const scrubbedMessage = typeof breadcrumb.message === 'string' ? scrub(breadcrumb.message) : breadcrumb.message;
  const scrubbedData = breadcrumb.data ? redactFields(scrubStringsIn(breadcrumb.data)) : breadcrumb.data;
  return {
    ...breadcrumb,
    message: scrubbedMessage,
    data: scrubbedData,
  };
}
